import { Box, Typography } from '@mui/material'
import { TOUCH_MIN } from 'core/theme.tokens'
import React, { ReactNode } from 'react'

export interface DashboardMetricTileProps {
  label: string
  value: ReactNode
  valueColor?: string
  caption?: ReactNode
  icon?: ReactNode
  /** Soft wash when the number needs attention. */
  highlight?: boolean
}

/** Stat cell. Number is the focus; the label sits above it. */
export const DashboardMetricTile: React.FC<DashboardMetricTileProps> = ({
  label,
  value,
  valueColor = 'text.primary',
  caption,
  icon,
  highlight = false,
}) => (
  <Box
    sx={{
      px: 1.15,
      py: 0.6,
      width: '100%',
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'center',
      textAlign: 'left',
      gap: 0.25,
      bgcolor: 'transparent',
      boxShadow: highlight ? 'inset 3px 0 0 #e65100' : 'none',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, maxWidth: '100%', color: 'text.secondary' }}>
      {icon ? (
        <Box sx={{ display: 'flex', flexShrink: 0, color: 'inherit', '& .MuiSvgIcon-root': { fontSize: 15, color: 'inherit' } }}>{icon}</Box>
      ) : null}
      <Typography variant='caption' color='inherit' sx={{ lineHeight: 1.25 }}>
        {label}
      </Typography>
    </Box>
    <Typography
      component='div'
      fontWeight={650}
      sx={{
        fontSize: { xs: '0.95rem', md: '1.05rem' },
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
        overflowWrap: 'anywhere',
        color: (theme) => {
          const onDark = theme.palette.mode === 'dark'
          if (valueColor.startsWith('success')) return onDark ? theme.palette.success.light : theme.palette.success.dark
          if (valueColor.startsWith('error')) return onDark ? theme.palette.error.light : theme.palette.error.dark
          if (valueColor.startsWith('warning')) return onDark ? theme.palette.warning.light : theme.palette.warning.dark
          return theme.palette.text.primary
        },
      }}
    >
      {value}
    </Typography>
    {caption ? (
      <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 1.25, maxWidth: '100%' }}>
        {caption}
      </Typography>
    ) : null}
  </Box>
)

/** Thin full-width label between scoreboard groups. Text stays on the left, on white. */
export const DashboardMetricBand: React.FC<{ label: string }> = ({ label }) => (
  <Box
    data-metric-band=''
    sx={{
      gridColumn: '1 / -1',
      px: 1.15,
      py: 0.25,
      minHeight: 22,
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
      textAlign: 'left',
      bgcolor: 'background.paper',
    }}
  >
    <Typography
      component='span'
      sx={{
        fontSize: '0.65rem',
        fontWeight: 650,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'text.secondary',
        lineHeight: 1.2,
        textAlign: 'left',
      }}
    >
      {label}
    </Typography>
  </Box>
)

/**
 * One scoreboard. `fill` lets a short last row share the width
 * so no blank columns are left open.
 */
export const DashboardMetricRow: React.FC<{ children: ReactNode; fill?: boolean; flush?: boolean }> = ({
  children,
  fill = false,
  flush = false,
}) => (
  <Box
    sx={{
      display: 'grid',
      width: '100%',
      gridTemplateColumns: fill
        ? 'repeat(auto-fit, minmax(148px, 1fr))'
        : {
            xs: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          },
      bgcolor: 'background.paper',
      border: flush ? 0 : 1,
      borderColor: 'divider',
      borderRadius: flush ? 0 : 1,
      overflow: 'hidden',
      '& > *:not([data-metric-band])': {
        minWidth: 0,
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        borderRight: 1,
        borderBottom: 1,
        borderColor: 'divider',
        marginRight: '-1px',
        marginBottom: '-1px',
      },
      '& > [data-metric-band]': {
        gridColumn: '1 / -1',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
        textAlign: 'left',
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
      },
    }}
  >
    {children}
  </Box>
)

/** Title and actions sit on the page. Metric rows are the only cards. */
export const DashboardSection: React.FC<{
  title: string
  action?: ReactNode
  children: ReactNode
}> = ({ title, action, children }) => (
  <Box sx={{ width: '100%', maxWidth: '100%' }}>
    <Box
      sx={{
        mb: 0.75,
        minHeight: 32,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography variant='body2' component='h2' fontWeight={650} color='text.secondary'>
        {title}
      </Typography>
      {action ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: 'center',
            width: { xs: '100%', sm: 'auto' },
            '& .MuiButton-root': { minHeight: { xs: TOUCH_MIN, sm: 32 }, py: 0.25 },
            '& .MuiIconButton-root': {
              minHeight: { xs: TOUCH_MIN, sm: 32 },
              minWidth: { xs: TOUCH_MIN, sm: 32 },
            },
          }}
        >
          {action}
        </Box>
      ) : null}
    </Box>
    {children}
  </Box>
)
